import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Home from '../../views/Home/index.vue';
import { mobileGuard } from '@/utils/guards/mobileGuard';
import { contactGuard } from '@/utils/guards/contactGuard';
import { codeGuard } from '@/utils/guards/codeGuard';
import { emptyConfigurationGuard } from '@/utils/guards/emptyConfigurationGuard';
import { PageType } from '@/store/modules/transitions';
import { homeResolver } from '@/views/Home/Home.resolver';
import { houseSizeResolver } from '@/views/HouseSize/HouseSize.resolver';
import { RouteNames } from './router.models';
import i18n from '@/i18n';
import { summaryResolver } from '@/views/Summary/Summary.resolver';
import { localeGuard } from '../guards/localeGuard';
import { ContactType } from '@/views/Contact/components/ContactForm/ContactForm.models';

Vue.use(VueRouter);

const routes: RouteConfig[] = [];

export const getPublicRoutes = (locale: string): RouteConfig[] => [
  {
    path: '/:lang' + i18n.t('global.routes.Home.path', locale),
    name: i18n.t('global.routes.Home.name', locale) as string,
    component: Home,
    beforeEnter: homeResolver,
    meta: {
      showProcessBar: false,
      showLogo: true,
      type: PageType.Overview
    }
  },
  {
    path: '/:lang' + i18n.t('global.routes.HouseSize.path', locale),
    name: i18n.t('global.routes.HouseSize.name', locale) as string,
    component: () => import(/* webpackChunkName: 'HouseSize' */ '../../views/HouseSize/index.vue'),
    beforeEnter: houseSizeResolver,
    meta: {
      showProcessBar: true,
      showCodeLink: true,
      showLogo: true,
      type: PageType.Outside
    }
  },
  {
    path: '/:lang' + i18n.t('global.routes.RoofShape.path', locale),
    name: i18n.t('global.routes.RoofShape.name', locale) as string,
    component: () => import(/* webpackChunkName: 'RoofShape' */ '../../views/RoofShape/index.vue'),
    meta: {
      showProcessBar: true,
      showCodeLink: true,
      showLogo: true,
      type: PageType.Outside
    }
  },
  {
    path: '/:lang' + i18n.t('global.routes.Basement.path', locale),
    name: i18n.t('global.routes.Basement.name', locale) as string,
    component: () => import(/* webpackChunkName: 'Basement' */ '../../views/Basement/index.vue'),
    meta: {
      showProcessBar: true,
      showCodeLink: true,
      showLogo: true,
      type: PageType.Inside
    }
  },
  {
    path: '/:lang' + i18n.t('global.routes.GroundFloor.path', locale),
    name: i18n.t('global.routes.GroundFloor.name', locale) as string,
    component: () => import(/* webpackChunkName: 'GroundFloor' */ '../../views/GroundFloor/index.vue'),
    meta: {
      showProcessBar: true,
      showCodeLink: true,
      showLogo: true,
      type: PageType.Inside
    }
  },
  {
    path: '/:lang' + i18n.t('global.routes.UpperFloor.path', locale),
    name: i18n.t('global.routes.UpperFloor.name', locale) as string,
    component: () => import(/* webpackChunkName: 'UpperFloor' */ '../../views/UpperFloor/index.vue'),
    meta: {
      showProcessBar: true,
      showCodeLink: true,
      showLogo: true,
      type: PageType.Inside
    }
  },
  {
    path: '/:lang' + i18n.t('global.routes.Attic.path', locale),
    name: i18n.t('global.routes.Attic.name', locale) as string,
    component: () => import(/* webpackChunkName: 'Attic' */ '../../views/Attic/index.vue'),
    meta: {
      showProcessBar: true,
      showCodeLink: true,
      showLogo: true,
      type: PageType.Inside
    }
  },
  {
    path: '/:lang' + i18n.t('global.routes.SummaryRoot.path', locale),
    name: i18n.t('global.routes.SummaryRoot.name', locale) as string,
    component: () => import(/* webpackChunkName: 'Summary' */ '../../views/Summary/index.vue'),
    beforeEnter: summaryResolver,
    meta: {
      showProcessBar: false,
      showLogo: true,
      type: PageType.Overview
    },
    children: [
      {
        path: '',
        name: i18n.t('global.routes.Summary.name', locale) as string,
        component: () =>
          import(/* webpackChunkName: 'SummaryOverview' */ '../../views/Summary/components/SummaryOverview/index.vue'),
        meta: {
          showProcessBar: false,
          showLogo: true,
          type: PageType.Overview
        }
      },
      {
        path: i18n.t('global.routes.SummaryFloorGroundFloor.path', locale) as string,
        name: i18n.t('global.routes.SummaryFloorGroundFloor.name', locale) as string,
        component: () =>
          import(/* webpackChunkName: 'SummaryOverview' */ '../../views/Summary/components/FloorOverview/index.vue'),
        meta: {
          showProcessBar: false,
          showLogo: true,
          type: PageType.Overview
        },
        props: {
          floorGetter: 'groundFloorOverview'
        }
      },
      {
        path: i18n.t('global.routes.SummaryFloorUpperFloor.path', locale) as string,
        name: i18n.t('global.routes.SummaryFloorUpperFloor.name', locale) as string,
        component: () =>
          import(/* webpackChunkName: 'SummaryOverview' */ '../../views/Summary/components/FloorOverview/index.vue'),
        meta: {
          showProcessBar: false,
          showLogo: true,
          type: PageType.Overview
        },
        props: {
          floorGetter: 'upperFloorOverview'
        }
      },
      {
        path: i18n.t('global.routes.SummaryFloorAttic.path', locale) as string,
        name: i18n.t('global.routes.SummaryFloorAttic.name', locale) as string,
        component: () =>
          import(/* webpackChunkName: 'SummaryOverview' */ '../../views/Summary/components/FloorOverview/index.vue'),
        meta: {
          showProcessBar: false,
          showLogo: true,
          type: PageType.Overview
        },
        props: {
          floorGetter: 'atticOverview'
        }
      }
    ]
  },
  {
    path: ('/:lang' + i18n.t('global.routes.ContactAppointment.path', locale)) as string,
    name: i18n.t('global.routes.ContactAppointment.name', locale) as string,
    component: () => import(/* webpackChunkName: 'Contact' */ '../../views/Contact/index.vue'),
    beforeEnter: contactGuard,
    meta: {
      showProcessBar: false,
      showCodeLink: false,
      showLogo: true,
      type: PageType.Overview,
      contactType: ContactType.Appointment
    }
  },
  {
    path: ('/:lang' + i18n.t('global.routes.ContactOffer.path', locale)) as string,
    name: i18n.t('global.routes.ContactOffer.name', locale) as string,
    component: () => import(/* webpackChunkName: 'Contact' */ '../../views/Contact/index.vue'),
    beforeEnter: contactGuard,
    meta: {
      showProcessBar: false,
      showCodeLink: false,
      showLogo: true,
      type: PageType.Overview,
      contactType: ContactType.Offer
    }
  },
  {
    path: ('/:lang' + i18n.t('global.routes.ContactConfirmation.path', locale)) as string,
    name: i18n.t('global.routes.ContactConfirmation.name', locale) as string,
    component: () =>
      import(/* webpackChunkName: 'Contact' */ '../../views/Contact/components/ContactConfirmation/index.vue'),
    beforeEnter: contactGuard,
    meta: {
      showProcessBar: false,
      showCodeLink: false,
      showLogo: true,
      type: PageType.Overview
    }
  }
];

export const publicRoutes: RouteConfig[] = [...getPublicRoutes('de-DE'), ...getPublicRoutes('en-GB')];

export const getFlatPublicRoutes = (locale: string) => {
  return getPublicRoutes(locale).reduce<RouteConfig[]>((acc, curr) => {
    acc.push(curr);
    if (curr.children) acc.push(...curr.children);
    return acc;
  }, []);
};

const logicRoutes: RouteConfig[] = [
  {
    path: '/:lang/mobile',
    name: RouteNames.MobileHome,
    component: () => import(/* webpackChunkName: 'MobileHome' */ '../../views/MobileHome/index.vue'),
    meta: {
      showProcessBar: false
    }
  },
  {
    path: '/:lang/:code',
    name: RouteNames.Code,
    beforeEnter: codeGuard
  }
];

routes.push(...publicRoutes, ...logicRoutes);

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
});

router.beforeEach(mobileGuard);
router.beforeEach(localeGuard);
router.beforeEach(emptyConfigurationGuard);

export default router;
